import { usercontainerConstants, USERCONTAINER_UNKNOWN_OUTCOME } from "../constants";
import { usercontainerService } from "../services";

// One thunk per operation (same shape as newspin.actions.js), but the service
// resolves to { ok, status, body } instead of a bare parsed body, so every
// thunk decides success/failure here from the HTTP status - never from
// body.message (random error-number prefix) and never from body.status (an
// enum name string such as "BAD_REQUEST").
export const usercontainerActions = {
    getMyContainers,
    create,
    deleteContainer,
    order,
    clear
};

// The machine-readable error key, e.g. "container.identity.missing". Absent on
// a non-JSON or empty error body, which is fine - callers fall back to status.
function toErrorCode(body) {
    if (body && Array.isArray(body.errors) && body.errors.length > 0) {
        return body.errors[0];
    }
    return undefined;
}

function toFailure(op, result) {
    return { op: op, status: result.status, errorCode: toErrorCode(result.body) };
}

// usercontainer.service.js resolves every call (see settled() there), so the
// terminal .catch on the three thunks below is defence in depth only: it covers
// a throw from a dispatch or a reducer, after which no SUCCESS and no FAILURE
// would have been dispatched and usercontainer.reducer.js clears `loading` in
// those cases ONLY - leaving CardConfigPopup's `busy` true in the store, past a
// close and a reopen. Each catch dispatches the operation's existing FAILURE
// action, so the flag cannot stick.
//
// There is no response to report on that path, and status 0 is already the
// service's "no response at all" marker (a rejected fetch, or a body that could
// not be read), so no new action type and no new payload field is needed: the
// same { ok, status, body } the service always resolves with is also what the
// caller's .then() keeps receiving, which is what clears the popup's own local
// `saving` flag.
function noResult() {
    return { ok: false, status: 0, body: undefined };
}

function getMyContainers(uuid, language) {

    return dispatch => {
        dispatch({ type: usercontainerConstants.USERCONTAINER_LIST_REQUEST });

        return usercontainerService.getMyContainers(uuid, language)
            .then(result => {
                if (!result.ok) {
                    dispatch({ type: usercontainerConstants.USERCONTAINER_LIST_FAILURE, payload: toFailure('list', result) });
                    return result;
                }
                dispatch({ type: usercontainerConstants.USERCONTAINER_LIST_SUCCESS, payload: result.body });
                return result;
            })
            .catch(() => {
                const result = noResult();
                dispatch({ type: usercontainerConstants.USERCONTAINER_LIST_FAILURE, payload: toFailure('list', result) });
                return result;
            });
    };
}

// A 200 with created: false (duplicate title for this user+language) is an
// idempotent success and dispatches SUCCESS, not FAILURE.
function create(user, title, language) {

    return dispatch => {
        dispatch({ type: usercontainerConstants.USERCONTAINER_CREATE_REQUEST });

        return usercontainerService.create(user, title, language)
            .then(result => {
                if (result.outcome === USERCONTAINER_UNKNOWN_OUTCOME) {
                    // Aborted after 60s or the network dropped: the container
                    // may already exist. Not a failure - re-fetch the list.
                    dispatch({ type: usercontainerConstants.USERCONTAINER_CREATE_UNKNOWN });
                    return result;
                }
                if (!result.ok) {
                    dispatch({ type: usercontainerConstants.USERCONTAINER_CREATE_FAILURE, payload: toFailure('create', result) });
                    return result;
                }
                dispatch({ type: usercontainerConstants.USERCONTAINER_CREATE_SUCCESS, payload: result.body });
                return result;
            });
    };
}

function deleteContainer(user, id) {

    return dispatch => {
        dispatch({ type: usercontainerConstants.USERCONTAINER_DELETE_REQUEST });

        return usercontainerService.deleteContainer(user, id)
            .then(result => {
                if (!result.ok) {
                    dispatch({ type: usercontainerConstants.USERCONTAINER_DELETE_FAILURE, payload: toFailure('delete', result) });
                    return result;
                }
                // The id removed has to come from the request payload, since
                // the response does not repeat it.
                //
                // The ...result.body spread is NOT incidental: the delete
                // response also carries `themes` (the refreshed { used,
                // allowed } allowance), and this spread is the only reason it
                // reaches the reducer at all. Tidying this into named fields -
                // payload: { deleted: ..., id: id } - would compile, pass every
                // existing check, and silently stop the allowance refreshing
                // after a delete, which is the single failure the backend emits
                // `themes` on delete to prevent: the counter would keep saying
                // the user is at their limit after they just made room.
                // getMyContainers (:59) and create (:89) pass result.body
                // straight through and are safe by construction; only this one
                // reshapes the body.
                dispatch({ type: usercontainerConstants.USERCONTAINER_DELETE_SUCCESS, payload: { ...result.body, id: id } });
                return result;
            })
            .catch(() => {
                const result = noResult();
                dispatch({ type: usercontainerConstants.USERCONTAINER_DELETE_FAILURE, payload: toFailure('delete', result) });
                return result;
            });
    };
}

// `list` is the full ordered render list; the service projects it down to
// [{ kind, name }] before it ever reaches the wire.
function order(user, language, list) {

    return dispatch => {
        dispatch({ type: usercontainerConstants.USERCONTAINER_ORDER_REQUEST });

        return usercontainerService.order(user, language, list)
            .then(result => {
                if (!result.ok) {
                    dispatch({ type: usercontainerConstants.USERCONTAINER_ORDER_FAILURE, payload: toFailure('order', result) });
                    return result;
                }
                dispatch({ type: usercontainerConstants.USERCONTAINER_ORDER_SUCCESS, payload: result.body });
                return result;
            })
            .catch(() => {
                const result = noResult();
                dispatch({ type: usercontainerConstants.USERCONTAINER_ORDER_FAILURE, payload: toFailure('order', result) });
                return result;
            });
    };
}

function clear() {
    return { type: usercontainerConstants.USERCONTAINER_CLEAR };
}
